import React from 'react';
import { Button } from '@/components/ui/button';
import Icon from '@/components/ui/Icon';
import { 
  Sparkles, 
  BarChart3, 
  Shield, 
  Mail,
  Bell
} from 'lucide-react';
import { useThemeSettings } from '@/contexts/ThemeContext';

const AIDiscoveryIntroCard = ({ 
  onConnectEmail, 
  variant = "dashboard",
  className = "",
  isEmailConnected = false,
  discoveredCampsCount = 0,
  messagesCount = 0
}) => {
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';

  return (
    <div className={`${isDarkTheme ? 'bg-slate-900' : 'bg-white'} rounded-xl shadow overflow-hidden border ${isDarkTheme ? 'border-slate-700/40' : 'border-purple-100/70'} ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-100 p-6">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className={`text-2xl font-bold ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>
              Email Scout
            </h2>
          </div>
        </div>
        {/* Subtext with icon */}
        <p className={`text-xs sm:text-sm ${isDarkTheme ? 'text-slate-300' : 'text-gray-600'} flex items-center gap-1.5`}>
          <Icon name="search" size="sm" />
          {isEmailConnected 
            ? `${discoveredCampsCount} camps discovered${messagesCount > 0 ? ` • ${messagesCount} messages scanned` : ''}`
            : 'Discover Your Summer Camps with AI'
          }
        </p>
      </div>

      {/* Content */}
      <div className="p-5 md:p-8">

        {/* Feature Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-5 md:mb-8">
          {/* Historical Analysis */}
          <div className={`rounded-xl p-4 md:p-6 border ${isDarkTheme ? 'bg-slate-800 border-slate-700/40' : 'bg-white border-purple-200'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 ${isDarkTheme ? 'bg-purple-900/30' : 'bg-purple-100'} rounded-lg`}>
                <BarChart3 className={`w-5 h-5 ${isDarkTheme ? 'text-purple-400' : 'text-brand-600'}`} />
              </div>
              <h3 className={`font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-purple-900'}`}>Instant Planning Kickstart</h3>
            </div>
            <p className={`text-xs md:text-sm ${isDarkTheme ? 'text-slate-300' : 'text-brand-700'}`}>
              Scan your email history to find camps you've registered for or been considering. Get organized in seconds with one click.
            </p>
          </div>

          {/* Daily Monitoring */}
          <div className={`rounded-xl p-4 md:p-6 border ${isDarkTheme ? 'bg-slate-800 border-slate-700/40' : 'bg-white border-blue-200'}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 ${isDarkTheme ? 'bg-blue-900/30' : 'bg-blue-100'} rounded-lg`}>
                <Bell className={`w-5 h-5 ${isDarkTheme ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <h3 className={`font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-blue-900'}`}>Ongoing Camp Tracking</h3>
            </div>
            <p className={`text-xs md:text-sm ${isDarkTheme ? 'text-slate-300' : 'text-blue-700'}`}>
              Stay updated as new camp emails arrive. Automatically capture registrations, schedule changes, and important deadlines.
            </p>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mb-3 md:mb-4">
          <Button
            onClick={onConnectEmail}
            className="bg-gradient-to-r from-brand-600 to-blue-600 hover:from-brand-700 hover:to-blue-700 text-white text-sm md:text-base px-5 py-2 md:px-7 md:py-3 font-semibold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <Mail className="w-5 h-5 mr-2" />
            Connect Gmail
          </Button>
        </div>

        {/* Privacy Note */}
        <div className="flex items-center justify-center gap-2 text-[11px] md:text-sm text-gray-500">
          <Shield className="w-4 h-4" />
          <span>Secure OAuth authentication. We only read camp-related emails and never share your data.</span>
        </div>
      </div>
    </div>
  );
};

export default AIDiscoveryIntroCard;
